import type { TopicStats } from "@/api/types";

/**
 * Attainment model (PRD `docs/records/prd-platform-evolution.md` §6.3).
 *
 * Attainment in plain UK teacher language. Rings express knowledge; the focus
 * stage trees (`lib/focusCapacity.ts`) express focus. Never mix the two.
 */

export type AttainmentTier =
  "not_started" | "learning" | "strong" | "secure" | "needs_review";

/**
 * Display order for the attainment spread on the subject page: strongest
 * first, then the two states that need action, then untouched.
 */
export const ATTAINMENT_ORDER: readonly AttainmentTier[] = [
  "secure",
  "strong",
  "learning",
  "needs_review",
  "not_started",
];

export const ATTAINMENT_LABEL: Record<AttainmentTier, string> = {
  not_started: "Not started",
  learning: "Learning",
  strong: "Strong",
  secure: "Secure",
  needs_review: "Needs review",
};

/** Percentage floors. A topic at exactly 55 is Strong; at exactly 80, Secure. */
export const STRONG_FLOOR_PCT = 55;
export const SECURE_FLOOR_PCT = 80;

/**
 * A topic goes stale after this long without an attempt. One decay
 * definition in the product, not two (PRD §13). Changing it changes both "Needs
 * review" and nothing else.
 */
export const DECAY_DAYS = 30;

const DAY_MS = 86_400_000;

/** True once `last_attempted_at` is older than {@link DECAY_DAYS}. */
export function isStale(
  stats: TopicStats | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!stats?.last_attempted_at) return false;
  const last = new Date(stats.last_attempted_at).getTime();
  if (Number.isNaN(last)) return false;
  return last < now.getTime() - DECAY_DAYS * DAY_MS;
}

/**
 * Attainment tier for one topic.
 *
 * Evaluation order is load-bearing and matches the design handoff exactly:
 * not_started → needs_review → secure → strong → learning. "Needs review"
 * deliberately outranks Secure so a stale-but-strong topic surfaces as
 * something to act on rather than hiding behind a green ring.
 */
export function tierFor(
  stats: TopicStats | null | undefined,
  now: Date = new Date(),
): AttainmentTier {
  if (!stats || stats.questions_attempted === 0) return "not_started";
  if (stats.avg_pct >= STRONG_FLOOR_PCT && isStale(stats, now))
    return "needs_review";
  if (stats.avg_pct >= SECURE_FLOOR_PCT) return "secure";
  if (stats.avg_pct >= STRONG_FLOOR_PCT) return "strong";
  return "learning";
}

export type AttainmentCounts = Record<AttainmentTier, number>;

/** Aggregate tier counts — feeds the Knowledge card's attainment spread. */
export function attainmentCounts(
  topics: readonly (TopicStats | null | undefined)[],
  now: Date = new Date(),
): AttainmentCounts {
  const counts: AttainmentCounts = {
    not_started: 0,
    learning: 0,
    strong: 0,
    secure: 0,
    needs_review: 0,
  };
  for (const s of topics) counts[tierFor(s, now)]++;
  return counts;
}

/** One topic's contribution to a subject's headline knowledge number. */
export interface WeightedTopic {
  /** Mean percentage across attempts. Ignored when `attempts` is 0. */
  avg_pct: number;
  attempts: number;
  /** `CourseTopic.exam_weighting_pct`; null on courses whose spec had no weights. */
  exam_weighting_pct: number | null;
}

/**
 * Subject knowledge %, weighted by exam weight (PRD §6.3 PE-3.3).
 *
 * Weighting matters because the topic row renders an exam-weight bar right
 * next to the ring: an unweighted mean would tell a student a 3.5%-weight
 * topic counts as much as a 9% one, contradicting the bar beside it.
 *
 * - Topics with no attempts are excluded entirely — they are reported
 *   separately as "not started", never averaged in as zero.
 * - If no attempted topic carries a weight (a course ingested without spec
 *   weightings), this falls back to an unweighted mean rather than returning
 *   0, which would read as "you know nothing".
 * - Returns 0 when nothing has been attempted at all.
 */
export function knowledgePct(topics: readonly WeightedTopic[]): number {
  const attempted = topics.filter((t) => t.attempts > 0);
  if (attempted.length === 0) return 0;

  let weightedSum = 0;
  let totalWeight = 0;
  for (const t of attempted) {
    const w = t.exam_weighting_pct ?? 0;
    if (w <= 0) continue;
    weightedSum += t.avg_pct * w;
    totalWeight += w;
  }

  if (totalWeight > 0) return Math.round(weightedSum / totalWeight);

  const plainSum = attempted.reduce((acc, t) => acc + t.avg_pct, 0);
  return Math.round(plainSum / attempted.length);
}

/**
 * Meta line for a subject row on Today, e.g. "2 to review · 3 learning · 1 not
 * started". Returns null when every topic is in good shape so the caller can
 * substitute its own copy rather than rendering an empty string.
 */
export function attainmentSummary(counts: AttainmentCounts): string | null {
  const bits: string[] = [];
  if (counts.needs_review) bits.push(`${counts.needs_review} to review`);
  if (counts.learning) bits.push(`${counts.learning} learning`);
  if (counts.not_started) bits.push(`${counts.not_started} not started`);
  return bits.length > 0 ? bits.join(" · ") : null;
}

/**
 * Ring tier for a whole subject. **The only definition** — the sidebar, Today's
 * comparison rows and the subject page all call this.
 *
 * It bands on `knowledgePct` rather than on the spread because the subject ring
 * renders that exact number inside itself on the subject page. A ring coloured
 * amber around a green-worthy "61%" is a contradiction the student can see, and
 * two functions banding two different ways would let the sidebar and the
 * subject page disagree about the same subject.
 *
 * `counts` is consulted only for the two things a percentage cannot express:
 * whether anything has been attempted at all, and whether *everything*
 * attempted has gone stale. A dashed ring means "this whole subject has gone
 * cold", which is only true when nothing in it is fresh — one stale topic among
 * ten does not dash the subject.
 *
 * With no counts (a frontend deployed ahead of D6) it falls back to the bands
 * alone; staleness is unknowable from a percentage, so that path never returns
 * `needs_review`.
 */
export function subjectTierFor(
  knowledgePct: number,
  counts?: Partial<AttainmentCounts> | null,
): AttainmentTier {
  if (counts) {
    const attempted =
      (counts.secure ?? 0) +
      (counts.strong ?? 0) +
      (counts.learning ?? 0) +
      (counts.needs_review ?? 0);
    if (attempted === 0) return "not_started";
    if ((counts.needs_review ?? 0) === attempted) return "needs_review";
  } else if (knowledgePct <= 0) {
    return "not_started";
  }

  if (knowledgePct >= SECURE_FLOOR_PCT) return "secure";
  if (knowledgePct >= STRONG_FLOOR_PCT) return "strong";
  return "learning";
}
